import React, { PropsWithChildren } from "react";
import { PerformerType } from "component-library";
import { useTranslation } from "i18n";
import { dataTestIds } from "data-testids";
import { getUserAttribute, UserAttributes } from "shared-auth";
import { isTruthy } from "shared-utils";
import {
  sendJobsfeedUsageHeapEventWithUserPrefix,
  JobsFeedUsageEvents,
  FEATURE_FLAGS,
  useLaunchDarklyFlags,
} from "shared-services";
import { Pill } from "../../../../Atoms/Pill/Pill";
import GenericAppShell, { GenericAppShellProps } from "../GenericAppShell";
import LeftHandNavItem from "../LeftHandNavItem";
import NavItem from "../NavItem";
import NavSearch from "../NavSearch";
import QuickActionButton from "../QuickActionButton";
import { ThemeContextValues } from "../NavThemeContext";
import {
  CogSettingsIcon as CogIcon,
  HelpQuestionIcon as HelpIcon,
  MegaphoneIcon,
  SearchIcon,
  StarOutlineIcon as StarIcon,
  UserOutlineIcon as UserIcon,
} from "assets";
import CdAgentSearchAutocomplete from "../CdAppShell/CdAgentSearchAutocomplete";
import { getAgentSearchRedirect } from "../CdAppShell/helpers/getAgentSearchRedirect";
import ViewJobsOnFilmmakersLeftHandNavItem from "../../../../Atoms/ViewJobsOnFilmmakersButtonWrapper/ViewJobsOnFilmmakersLeftHandNavItem";

const searchRedirect = (searchTerm: string) =>
  window.location.replace(getAgentSearchRedirect(searchTerm));

const AgentAppShell = ({
  footerComponent,
  hideFooter,
  children,
}: PropsWithChildren<
  Pick<GenericAppShellProps, "hideFooter" | "footerComponent">
>) => {
  const { t } = useTranslation();
  const isHideMessageNavItemEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.HideMessageNavItem
  );

  const canRepresentYoungPerformers = isTruthy(
    getUserAttribute(UserAttributes.AgentCanRepYps)
  );

  return (
    <GenericAppShell
      theme={ThemeContextValues.Agent}
      hideFooter={hideFooter}
      footerComponent={footerComponent}
      leftHandNavItems={
        <>
          <LeftHandNavItem
            to="/jobs/matching-my-filters"
            icon={SearchIcon}
            onClick={() =>
              sendJobsfeedUsageHeapEventWithUserPrefix(
                JobsFeedUsageEvents.LhnMatchingMyFilter
              )
            }
          >
            {t("common:nav.links.jobsFeed.filters")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="/jobs/all-opportunities"
            isSubNav
            onClick={() =>
              sendJobsfeedUsageHeapEventWithUserPrefix(
                JobsFeedUsageEvents.LhnAllOpportunities
              )
            }
          >
            {t("common:nav.links.projects")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/jobs/client-alerts" isSubNav>
            {t("common:nav.links.clientAlerts")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/jobs/self-tape-requests" isSubNav>
            {t("common:nav.links.mediaRequests")}
            <Pill className="bg-blue-sky-2 ml-1.5" theme="light" size="md">
              {t("common:nav.links.pill.beta")}
            </Pill>
          </LeftHandNavItem>
          {!isHideMessageNavItemEnabled && (
            <LeftHandNavItem to="/agentweb/feed/messages" isSubNav>
              {t("common:nav.links.messages")}
            </LeftHandNavItem>
          )}
          <LeftHandNavItem
            to="/settings/jobs-feed"
            isSubNav
            onClick={() =>
              sendJobsfeedUsageHeapEventWithUserPrefix(
                JobsFeedUsageEvents.LhnJobsfeedSettings
              )
            }
          >
            {t("common:nav.links.jobsFeedSettings")}
          </LeftHandNavItem>

          <ViewJobsOnFilmmakersLeftHandNavItem />

          <LeftHandNavItem to="/clients" icon={UserIcon}>
            {t("common:nav.links.clientList")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/agentweb/EmailCvs" isSubNav>
            {t("common:nav.links.emailClientCvs")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/searchsuggestions" isSubNav>
            {t("common:nav.links.searchSuggestions")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/clients/pin-list/filtered" isSubNav>
            {t("common:nav.links.clientPins")}
          </LeftHandNavItem>
          <LeftHandNavItem to="/clientaccess" isSubNav>
            {t("common:nav.links.clientAccessLevel")}
          </LeftHandNavItem>
          {canRepresentYoungPerformers && (
            <LeftHandNavItem to="clients/yp-application" isSubNav>
              {t("common:nav.links.youngPerformerApplications")}
            </LeftHandNavItem>
          )}

          <LeftHandNavItem to="/clients/seeking-representation" icon={StarIcon}>
            {t("common:nav.links.seekingRepresentation")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://spotlight-showcase.com" isSubNav>
            {t("common:nav.links.digitalShowcase")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://calendar.spotlight.com" isSubNav>
            {t("common:nav.links.gradPerformanceCalendar")}
          </LeftHandNavItem>

          <LeftHandNavItem
            to="https://www.spotlight.com/help-and-faqs/links-for-professionals-on-spotlight/"
            icon={MegaphoneIcon}
          >
            {t("common:nav.links.industryResources")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/news-and-advice/"
            isSubNav
          >
            {t("common:nav.links.newsAndAdvice")}
          </LeftHandNavItem>
          <LeftHandNavItem to="https://app.spotlight.com/contacts" isSubNav>
            {t("common:nav.links.contactsListings")}
          </LeftHandNavItem>
          <LeftHandNavItem
            to="https://www.spotlight.com/members/spotlight-performer-events/"
            isSubNav
          >
            {t("common:nav.links.clientEvents")}
          </LeftHandNavItem>
        </>
      }
      dynamicHeaderContent={
        <>
          <NavSearch
            submitSearch={searchRedirect}
            renderAutoCompleteResult={(searchTerm) => (
              <CdAgentSearchAutocomplete
                searchTerm={searchTerm}
                searchRedirect={searchRedirect}
                performerTypes={[
                  PerformerType.Performer,
                  PerformerType.Graduate,
                  PerformerType.Filmmakers,
                ]}
              />
            )}
          />
          <QuickActionButton
            label={t("common:nav.links.jobsFeed")}
            linkUrl="/jobs/matching-my-filters"
          />
        </>
      }
      userMenuItems={
        <>
          <NavItem
            to="/settings"
            icon={CogIcon}
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.MainNavigationItem.mainNavigationLink"
              ]
            }
          >
            {t("common:nav.links.accountSettings")}
          </NavItem>
          <NavItem
            to="https://portal.spotlight.com/portal/account/details"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.agentUserDetails")}
          </NavItem>
          <NavItem
            to="/clients/agency"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.agencyDetails")}
          </NavItem>

          <NavItem
            to="https://www.spotlight.com/the-small-print/help-for-agents-using-the-spotlight-link/"
            icon={HelpIcon}
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.MainNavigationItem.mainNavigationLink"
              ]
            }
          >
            {t("common:nav.links.helpAndTutorials")}
          </NavItem>
          <NavItem
            to="https://support.spotlight.com"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.support")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/help-and-faqs/a-guide-to-spotlight-for-agents-onboarding-new-clients/"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.clientOnboarding")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/the-small-print/terms-conditions/"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.agent.termsAndConditions")}
          </NavItem>
          <NavItem
            to="https://www.spotlight.com/rooms-and-studios/"
            data-testid={
              dataTestIds.componentLibrary[
                "Organisms.NavigationHeader.SubNavigationItem.subNavigationLink"
              ]
            }
            isSubNav
          >
            {t("common:nav.links.hireRoomsStudios")}
          </NavItem>
        </>
      }
    >
      {children}
    </GenericAppShell>
  );
};

export default AgentAppShell;
